// Stock des intrants : fers, ciment, béton 8/16, sable… (réceptions, sorties, inventaire, catalogue)
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Grid, IconButton, MenuItem, Snackbar, Switch, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import { DeleteOutline, EditOutlined, MoveToInboxOutlined, OutboxOutlined, FactCheckOutlined, AddOutlined } from '@mui/icons-material';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { intrantAPI } from '../services/api';
import { apiError, dateFr, today } from '../utils/format';
import { tokens } from '../theme';
import { useFer, kgParBarre, enBarres } from '../utils/fer';

const qte = (n) => Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 3 });
const STATUT = {
  RUPTURE: { label: 'Rupture', color: 'error' },
  FAIBLE: { label: 'Stock faible', color: 'warning' },
  NORMAL: { label: 'Disponible', color: 'success' },
};
const TYPES = {
  ENTREE: { label: 'Réception', sign: '+', color: 'success.main' },
  SORTIE: { label: 'Sortie production', sign: '−', color: 'error.main' },
  AJUSTEMENT_POSITIF: { label: 'Inventaire (+)', sign: '+', color: 'info.main' },
  AJUSTEMENT_NEGATIF: { label: 'Inventaire (−)', sign: '−', color: 'warning.main' },
};
const CATEGORIES = { FER_IMPORTE: 'Fer importé', FER_LOCAL: 'Fer local', CIMENT: 'Ciment', GRANULAT: 'Granulats', AUTRE: 'Autres' };

/** Poids d'une barre pour un intrant fer suivi en kg (sinon null). */
const kpbIntrant = (i, t) => (i && i.unite === 'kg' && /^FER/.test(i.categorie) ? kgParBarre(i.code, i.categorie === 'FER_LOCAL' ? 'LOCAL' : 'IMPORTE', t) : null);
const fr1 = (n) => String(n).replace('.', ',');

function IntrantCard({ i, onIn, onOut, kpb }) {
  const s = STATUT[i.statut];
  return (
    <Card variant="outlined" sx={{ height: '100%', borderLeft: 4, borderLeftColor: `${s.color}.main` }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>{i.designation}</Typography>
          {i.statut !== 'NORMAL' && <Chip size="small" label={s.label} color={s.color} variant="outlined" />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 1 }}>
          <Typography sx={{ fontFamily: tokens.mono, fontSize: 26, fontWeight: 600, color: i.statut === 'RUPTURE' ? 'error.main' : 'text.primary' }}>
            {qte(i.stock)}
          </Typography>
          <Typography color="text.secondary">{i.unite}</Typography>
          {kpb && i.stock > 0 && <Typography color="text.secondary" sx={{ fontSize: 13 }}>≈ {qte(enBarres(i.stock, kpb))} barres</Typography>}
        </Box>
        <Typography variant="caption" color="text.secondary" component="div">
          Ce mois : +{qte(i.entrees_mois)} / −{qte(i.sorties_mois)} · seuil {qte(i.seuil_alerte)}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button size="small" startIcon={<MoveToInboxOutlined />} onClick={() => onIn(i)}>Réception</Button>
          <Button size="small" color="inherit" startIcon={<OutboxOutlined />} disabled={i.stock <= 0} onClick={() => onOut(i)}>Sortie</Button>
        </Box>
      </CardContent>
    </Card>
  );
}

const emptyMvt = (type, i) => ({ type_operation: type, intrant_id: i?.id || '', date: today(), quantite: '', barres: '', fournisseur: '', reference: '', commentaire: '' });

const StockIntrants = () => {
  const { canViewDashboard: isManager, canManageUsers: isAdmin } = useAuth();
  const ferT = useFer();
  const [tab, setTab] = useState(0);
  const [intrants, setIntrants] = useState([]);
  const [mouvements, setMouvements] = useState([]);
  const [filters, setFilters] = useState({ intrant_id: '', type: '', from: '', to: '' });
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [mvt, setMvt] = useState(null);         // dialogue réception / sortie
  const [inv, setInv] = useState(null);         // dialogue inventaire
  const [cat, setCat] = useState(null);         // dialogue catalogue
  const [dlgError, setDlgError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    intrantAPI.list(isAdmin).then((r) => setIntrants(r.data)).catch((e) => setError(apiError(e)));
  }, [isAdmin]);
  const loadMvts = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    intrantAPI.movements(params).then((r) => setMouvements(r.data)).catch((e) => setError(apiError(e)));
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMvts(); }, [loadMvts]);

  const actifs = useMemo(() => intrants.filter((i) => i.is_active), [intrants]);
  const groupes = useMemo(() => Object.keys(CATEGORIES)
    .map((c) => ({ c, items: actifs.filter((i) => i.categorie === c) }))
    .filter((g) => g.items.length), [actifs]);
  const alertes = actifs.filter((i) => i.statut !== 'NORMAL');
  const selected = actifs.find((i) => i.id === Number(mvt?.intrant_id));

  const run = async (fn, okMsg, close) => {
    setSaving(true); setDlgError('');
    try { await fn(); setToast(okMsg); close(); load(); loadMvts(); } catch (e) { setDlgError(apiError(e)); } finally { setSaving(false); }
  };

  const kpbSel = kpbIntrant(selected, ferT);
  const saveMvt = () => run(() => {
    const { barres, ...m } = mvt;
    const note = barres && kpbSel ? `${barres} barres × ${fr1(kpbSel)} kg` : '';
    return intrantAPI.addMovement({ ...m, commentaire: [note, m.commentaire].filter(Boolean).join(' — ') || m.commentaire });
  }, mvt.type_operation === 'ENTREE' ? 'Réception enregistrée' : 'Sortie enregistrée', () => setMvt(null));
  const openInv = () => { setDlgError(''); setInv({ date: today(), comptes: Object.fromEntries(actifs.map((i) => [i.id, ''])) }); };
  const saveInv = () => run(async () => {
    const lignes = Object.entries(inv.comptes).filter(([, v]) => v !== '').map(([id, v]) => ({ intrant_id: Number(id), quantite_comptee: v }));
    if (!lignes.length) throw new Error('Saisissez au moins une quantité comptée');
    await intrantAPI.inventory({ date: inv.date, lignes });
  }, 'Inventaire enregistré : le stock a été recalé sur les quantités comptées', () => setInv(null));
  const saveCat = () => run(() => (cat.id ? intrantAPI.update(cat.id, cat) : intrantAPI.create(cat)), cat.id ? 'Intrant modifié' : 'Intrant ajouté', () => setCat(null));
  const removeMvt = async (m) => {
    if (!window.confirm(`Supprimer ce mouvement (${TYPES[m.type_operation].label} de ${qte(m.quantite)} ${m.unite} de ${m.designation}) ?`)) return;
    try { await intrantAPI.deleteMovement(m.id); setToast('Mouvement supprimé'); load(); loadMvts(); } catch (e) { setError(apiError(e)); }
  };

  const actions = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <Button variant="contained" startIcon={<MoveToInboxOutlined />} onClick={() => { setDlgError(''); setMvt(emptyMvt('ENTREE')); }}>Réception</Button>
      <Button variant="outlined" startIcon={<OutboxOutlined />} onClick={() => { setDlgError(''); setMvt(emptyMvt('SORTIE')); }}>Sortie</Button>
      {isManager && <Button variant="outlined" startIcon={<FactCheckOutlined />} onClick={openInv}>Inventaire</Button>}
    </Box>
  );

  return (
    <Layout title="Stock des intrants" actions={actions} plain>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="État du stock" />
        <Tab label="Mouvements" />
        {isAdmin && <Tab label="Catalogue" />}
      </Tabs>

      {tab === 0 && (
        <>
          {alertes.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              À réapprovisionner : {alertes.map((i) => `${i.designation} (${qte(i.stock)} ${i.unite})`).join(' · ')}
            </Alert>
          )}
          {groupes.map((g) => (
            <Box key={g.c} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>{CATEGORIES[g.c]}</Typography>
              <Grid container spacing={2}>
                {g.items.map((i) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={i.id}>
                    <IntrantCard i={i} kpb={kpbIntrant(i, ferT)}
                      onIn={(x) => { setDlgError(''); setMvt(emptyMvt('ENTREE', x)); }}
                      onOut={(x) => { setDlgError(''); setMvt(emptyMvt('SORTIE', x)); }} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </>
      )}

      {tab === 1 && (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <TextField select size="small" label="Intrant" value={filters.intrant_id} sx={{ minWidth: 180 }}
                onChange={(e) => setFilters({ ...filters, intrant_id: e.target.value })}>
                <MenuItem value="">Tous</MenuItem>
                {intrants.map((i) => <MenuItem key={i.id} value={i.id}>{i.designation}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Type" value={filters.type} sx={{ minWidth: 180 }}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
                <MenuItem value="">Tous</MenuItem>
                {Object.entries(TYPES).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </TextField>
              <TextField size="small" type="date" label="Du" InputLabelProps={{ shrink: true }} value={filters.from}
                onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
              <TextField size="small" type="date" label="Au" InputLabelProps={{ shrink: true }} value={filters.to}
                onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Intrant</TableCell>
                    <TableCell>Opération</TableCell>
                    <TableCell align="right">Quantité</TableCell>
                    <TableCell>Fournisseur / Réf.</TableCell>
                    <TableCell>Commentaire</TableCell>
                    <TableCell>Saisi par</TableCell>
                    {isManager && <TableCell />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mouvements.map((m) => (
                    <TableRow key={m.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateFr(m.date)}</TableCell>
                      <TableCell>{m.designation}</TableCell>
                      <TableCell>{TYPES[m.type_operation]?.label}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap', color: TYPES[m.type_operation]?.color }}>
                        {TYPES[m.type_operation]?.sign}{qte(m.quantite)} {m.unite}
                      </TableCell>
                      <TableCell>{[m.fournisseur, m.reference].filter(Boolean).join(' · ')}</TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>{m.commentaire}</TableCell>
                      <TableCell>{m.utilisateur}</TableCell>
                      {isManager && (
                        <TableCell padding="checkbox">
                          <Tooltip title={m.rapport_id ? 'Sortie du rapport journalier : corrigez le rapport' : 'Supprimer ce mouvement'}><span>
                            <IconButton size="small" aria-label="Supprimer ce mouvement" disabled={!!m.rapport_id} onClick={() => removeMvt(m)}><DeleteOutline fontSize="small" /></IconButton></span>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {mouvements.length === 0 && <TableRow><TableCell colSpan={8} align="center">Aucun mouvement</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {tab === 2 && isAdmin && (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography color="text.secondary" variant="body2">Unités et seuils d'alerte des intrants. Un intrant désactivé n'apparaît plus dans les saisies.</Typography>
              <Button startIcon={<AddOutlined />} onClick={() => { setDlgError(''); setCat({ code: '', designation: '', categorie: 'AUTRE', unite: '', seuil_alerte: 0, position: 100, is_active: true }); }}>Ajouter un intrant</Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell><TableCell>Désignation</TableCell><TableCell>Catégorie</TableCell>
                    <TableCell>Unité</TableCell><TableCell align="right">Seuil d'alerte</TableCell><TableCell>État</TableCell><TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {intrants.map((i) => (
                    <TableRow key={i.id} hover sx={{ opacity: i.is_active ? 1 : 0.55 }}>
                      <TableCell sx={{ fontFamily: tokens.mono }}>{i.code}</TableCell>
                      <TableCell>{i.designation}</TableCell>
                      <TableCell>{CATEGORIES[i.categorie]}</TableCell>
                      <TableCell>{i.unite}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{qte(i.seuil_alerte)}</TableCell>
                      <TableCell>{i.is_active ? 'Actif' : 'Désactivé'}</TableCell>
                      <TableCell padding="checkbox">
                        <IconButton size="small" aria-label={`Modifier ${i.designation}`} onClick={() => { setDlgError(''); setCat({ ...i }); }}><EditOutlined fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Réception / sortie */}
      <Dialog open={!!mvt} onClose={() => setMvt(null)} fullWidth maxWidth="xs">
        {mvt && (
          <>
            <DialogTitle>{mvt.type_operation === 'ENTREE' ? 'Réception d\'intrant' : 'Sortie pour la production'}</DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
              {dlgError && <Alert severity="error">{dlgError}</Alert>}
              <TextField select label="Intrant" value={mvt.intrant_id} required onChange={(e) => setMvt({ ...mvt, intrant_id: e.target.value })}>
                {actifs.map((i) => <MenuItem key={i.id} value={i.id}>{i.designation} — {qte(i.stock)} {i.unite}</MenuItem>)}
              </TextField>
              <TextField type="date" label="Date" value={mvt.date} required InputLabelProps={{ shrink: true }} onChange={(e) => setMvt({ ...mvt, date: e.target.value })} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField sx={{ flex: 1 }} label={`Quantité${selected ? ` (${selected.unite})` : ''}`} value={mvt.quantite} required inputMode="decimal"
                  helperText={mvt.type_operation === 'SORTIE' && selected ? `Disponible : ${qte(selected.stock)} ${selected.unite}${kpbSel ? ` (≈ ${qte(enBarres(selected.stock, kpbSel))} barres)` : ''}` : ' '}
                  onChange={(e) => setMvt({ ...mvt, quantite: e.target.value, barres: '' })} />
                {kpbSel && (
                  <TextField sx={{ width: 130 }} label="ou en barres" value={mvt.barres} inputProps={{ inputMode: 'numeric', 'aria-label': 'Nombre de barres' }}
                    helperText={`1 barre = ${fr1(kpbSel)} kg`}
                    onChange={(e) => { const b = e.target.value.replace(/[^\d]/g, ''); setMvt({ ...mvt, barres: b, quantite: b ? fr1(Math.round(Number(b) * kpbSel * 10) / 10) : '' }); }} />
                )}
              </Box>
              {mvt.type_operation === 'ENTREE' && (
                <TextField label="Fournisseur" value={mvt.fournisseur} onChange={(e) => setMvt({ ...mvt, fournisseur: e.target.value })} />
              )}
              <TextField label={mvt.type_operation === 'ENTREE' ? 'N° bon de livraison fournisseur' : 'Référence (bon de sortie, lot…)'} value={mvt.reference}
                onChange={(e) => setMvt({ ...mvt, reference: e.target.value })} />
              <TextField label="Commentaire" value={mvt.commentaire} multiline minRows={2} onChange={(e) => setMvt({ ...mvt, commentaire: e.target.value })} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMvt(null)}>Annuler</Button>
              <Button variant="contained" disabled={saving || !mvt.intrant_id || !mvt.quantite} onClick={saveMvt}>Enregistrer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Inventaire */}
      <Dialog open={!!inv} onClose={() => setInv(null)} fullWidth maxWidth="sm">
        {inv && (
          <>
            <DialogTitle>Inventaire des intrants</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
              {dlgError && <Alert severity="error" sx={{ mb: 2 }}>{dlgError}</Alert>}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Saisissez les quantités réellement comptées. Le stock sera recalé et chaque écart tracé. Laissez vide ce qui n'a pas été compté.
              </Typography>
              <TextField type="date" size="small" label="Date du comptage" value={inv.date} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}
                onChange={(e) => setInv({ ...inv, date: e.target.value })} />
              <Table size="small">
                <TableHead><TableRow><TableCell>Intrant</TableCell><TableCell align="right">Théorique</TableCell><TableCell>Compté</TableCell><TableCell align="right">Écart</TableCell></TableRow></TableHead>
                <TableBody>
                  {actifs.map((i) => {
                    const v = inv.comptes[i.id];
                    const ecart = v === '' ? null : Number(String(v).replace(',', '.')) - i.stock;
                    return (
                      <TableRow key={i.id}>
                        <TableCell>{i.designation}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap' }}>{qte(i.stock)} {i.unite}</TableCell>
                        <TableCell sx={{ width: kpbIntrant(i, ferT) ? 220 : 130 }}>
                          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                            <TextField size="small" value={v} inputProps={{ 'aria-label': `Compté ${i.designation}`, inputMode: 'decimal' }}
                              onChange={(e) => setInv({ ...inv, comptes: { ...inv.comptes, [i.id]: e.target.value }, barres: { ...(inv.barres || {}), [i.id]: '' } })} />
                            {kpbIntrant(i, ferT) && (
                              <TextField size="small" placeholder="barres" value={inv.barres?.[i.id] || ''} sx={{ width: 90 }}
                                inputProps={{ 'aria-label': `Barres comptées ${i.designation}`, inputMode: 'numeric' }}
                                onChange={(e) => { const b = e.target.value.replace(/[^\d]/g, ''); const k = kpbIntrant(i, ferT);
                                  setInv({ ...inv, barres: { ...(inv.barres || {}), [i.id]: b }, comptes: { ...inv.comptes, [i.id]: b ? String(Math.round(Number(b) * k * 10) / 10) : '' } }); }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: tokens.mono, color: ecart ? (ecart > 0 ? 'info.main' : 'error.main') : 'text.secondary' }}>
                          {ecart === null || Number.isNaN(ecart) ? '' : `${ecart > 0 ? '+' : ''}${qte(ecart)}`}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setInv(null)}>Annuler</Button>
              <Button variant="contained" disabled={saving} onClick={saveInv}>Valider l'inventaire</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Catalogue */}
      <Dialog open={!!cat} onClose={() => setCat(null)} fullWidth maxWidth="xs">
        {cat && (
          <>
            <DialogTitle>{cat.id ? `Modifier ${cat.designation}` : 'Nouvel intrant'}</DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
              {dlgError && <Alert severity="error">{dlgError}</Alert>}
              <TextField label="Code" value={cat.code} disabled={!!cat.id} required onChange={(e) => setCat({ ...cat, code: e.target.value })} />
              <TextField label="Désignation" value={cat.designation} required onChange={(e) => setCat({ ...cat, designation: e.target.value })} />
              <TextField select label="Catégorie" value={cat.categorie} onChange={(e) => setCat({ ...cat, categorie: e.target.value })}>
                {Object.entries(CATEGORIES).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </TextField>
              <TextField label="Unité (kg, t, sac, m³…)" value={cat.unite} required
                helperText={cat.nb_mouvements > 0 ? 'Non modifiable : des mouvements existent déjà' : ' '}
                disabled={cat.nb_mouvements > 0} onChange={(e) => setCat({ ...cat, unite: e.target.value })} />
              <TextField label="Seuil d'alerte" value={cat.seuil_alerte} inputMode="decimal" onChange={(e) => setCat({ ...cat, seuil_alerte: e.target.value })} />
              <TextField label="Ordre d'affichage" type="number" value={cat.position} onChange={(e) => setCat({ ...cat, position: e.target.value })} />
              <FormControlLabel control={<Switch checked={cat.is_active !== false} onChange={(e) => setCat({ ...cat, is_active: e.target.checked })} />} label="Actif" />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCat(null)}>Annuler</Button>
              <Button variant="contained" disabled={saving} onClick={saveCat}>Enregistrer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Layout>
  );
};

export default StockIntrants;
